import { BACKGROUNDS, getBackground } from "../../backgrounds/registry.js";
import { removeHeroImage, uploadBackgroundMedia, urlFor } from "../../firebase/storage.js";
import { useState } from "react";

const NONE = { id: null, label: "None", preview: "linear-gradient(135deg, #111 40%, #222)" };
const ALL = [NONE, ...BACKGROUNDS];

export function CustomBackgroundPicker({
  slug,
  customBackground,
  onCustomChange,
}) {
  const [busy, setBusy] = useState(false);
  const mediaType = customBackground?.type;

  async function onFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const uploaded = await uploadBackgroundMedia(slug, file);
      if (customBackground?.path) {
        try { await removeHeroImage(customBackground.path); } catch { /* best effort */ }
      }
      onCustomChange({
        path: uploaded.path,
        type: file.type.startsWith("video/") ? "video" : "image",
        name: file.name,
      });
    } finally {
      setBusy(false);
    }
  }

  async function removeCustom() {
    const path = customBackground?.path;
    if (path) {
      try { await removeHeroImage(path); } catch { /* best effort */ }
    }
    onCustomChange(null);
  }

  return (
    <div className="stack">
      <div className="custom-background-panel">
        <div className="row" style={{ alignItems: "flex-start" }}>
          <div className="stack" style={{ gap: 4 }}>
            <strong>Custom image or video</strong>
            <p className="muted" style={{ margin: 0, fontSize: ".86rem", lineHeight: 1.45 }}>
              Best for mobile: 9:16 portrait. Use 1080 × 1920 images, or MP4/WebM video at 1080 × 1920, under 15 seconds and compressed for quick WhatsApp loading.
            </p>
          </div>
        </div>
        {customBackground?.path && (
          <div className="custom-background-preview">
            {mediaType === "video" ? (
              <video src={urlFor(customBackground.path)} muted loop playsInline autoPlay />
            ) : (
              <img src={urlFor(customBackground.path)} alt="" />
            )}
            <div className="custom-background-meta">
              <span>{customBackground.name || "Custom background"}</span>
              <button type="button" className="btn-ghost btn mini" onClick={removeCustom}>Remove</button>
            </div>
          </div>
        )}
        <label className="btn btn-ghost" style={{ width: "fit-content" }}>
          {busy ? "Uploading…" : customBackground?.path ? "Replace media" : "Upload media"}
          <input type="file" accept="image/*,video/mp4,video/webm" onChange={onFile} disabled={busy} style={{ display: "none" }} />
        </label>
      </div>
    </div>
  );
}

export default function BackgroundPicker({
  customBackground,
  backgroundId,
  backgroundSetId,
  onChange,
}) {
  const active = getBackground(backgroundId);
  const sets = active?.sets ?? [];

  function pickBackground(id) {
    const meta = getBackground(id);
    onChange({ backgroundId: id, backgroundSetId: meta?.defaultSetId ?? null });
  }

  return (
    <div className="stack">
      <h3>Dynamic Background</h3>

      <div className="row" style={{ flexWrap: "wrap", gap: 12 }}>
        {ALL.map((b) => {
          const isActive = (b.id ?? null) === (backgroundId ?? null);
          return (
            <button
              key={b.id ?? "none"}
              type="button"
              title={b.label}
              onClick={() => pickBackground(b.id)}
              aria-pressed={isActive}
              style={{
                width: 132, height: 98, borderRadius: 8, padding: 7,
                border: isActive ? "2px solid var(--app-accent)" : "1px solid var(--app-border)",
                background: isActive ? "color-mix(in srgb, var(--app-accent) 14%, var(--app-surface))" : "var(--app-surface)",
                color: "var(--app-fg)",
                display: "grid",
                gridTemplateRows: "50px 1fr",
                gap: 8,
                alignItems: "center",
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: "100%",
                  height: 50,
                  borderRadius: 6,
                  background: b.preview,
                  boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--app-muted, #888)",
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {b.id === null ? "None" : ""}
              </span>
              <span
                style={{
                  display: "block",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: 1.15,
                  letterSpacing: 0,
                }}
              >
                {b.label}
              </span>
            </button>
          );
        })}
      </div>

      {customBackground?.path && (
        <p className="muted" style={{ margin: 0, fontSize: ".82rem" }}>
          Choosing a dynamic background will remove the uploaded custom media from the server after confirmation.
        </p>
      )}

      {active && sets.length > 1 && (
        <div className="row" style={{ flexWrap: "wrap", gap: 8, marginTop: 4 }}>
          {sets.map((s) => (
            <button
              key={s.id}
              className={s.id === (backgroundSetId ?? active.defaultSetId) ? "btn mini" : "btn-ghost btn mini"}
              onClick={() => onChange({ backgroundId, backgroundSetId: s.id })}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
